import React, { useState, useEffect } from "react";
import { useAuth } from "@/lib/AuthContext";
import { useEstateContext } from "@/lib/EstateContext";
import { useSubscription } from "@/hooks/useSubscription";
import { TrialBanner } from "@/components/TrialBanner";
import { useNavigate } from "react-router-dom";
import { createPageUrl } from "@/utils";
import { supabase } from "@/lib/supabase";
import { contractorsService, estatesService } from "@/services";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { 
  ArrowLeft, 
  Printer, 
  Loader2, 
  FileText, 
  AlertCircle,
  CheckCircle2,
  Clock
} from "lucide-react";
import { format } from "date-fns";
import { captureEvent } from "@/lib/analytics";

const categoryLabels = {
  pothole: "Pothole",
  blocked_drain: "Blocked Drain",
  waste_collection: "Waste Collection",
  street_lighting: "Street Lighting",
  flooding: "Flooding",
  vandalism: "Vandalism",
  illegal_dumping: "Illegal Dumping",
  broken_infrastructure: "Broken Infrastructure",
  water_leak: "Water Leak",
  electrical: "Electrical",
  plumbing: "Plumbing",
  security: "Security",
  general_maintenance: "General Maintenance",
  garden_landscape: "Garden/Landscape",
  structural: "Structural",
  other: "Other",
};

const statusColors = {
  reported: { bg: "bg-red-100", text: "text-red-700", icon: AlertCircle },
  acknowledged: { bg: "bg-yellow-100", text: "text-yellow-700", icon: Clock },
  in_progress: { bg: "bg-blue-100", text: "text-blue-700", icon: Clock },
  waiting_contractor: { bg: "bg-orange-100", text: "text-orange-700", icon: Clock },
  dependent_on: { bg: "bg-purple-100", text: "text-purple-700", icon: Clock },
  resolved: { bg: "bg-green-100", text: "text-green-700", icon: CheckCircle2 },
  closed: { bg: "bg-gray-100", text: "text-gray-700", icon: CheckCircle2 },
};

export default function MonthlyReport() {
  const navigate = useNavigate();
  const { user, isLoadingAuth } = useAuth();
  const { activeEstateId } = useEstateContext();
  const { subscription } = useSubscription();
  const [reportData, setReportData] = useState(null);
  const [contractors, setContractors] = useState([]);
  const [estateInfo, setEstateInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [bannerDismissed, setBannerDismissed] = useState(false);

  // Get estate_id from URL params, context, or user (legacy)
  const urlParams = new URLSearchParams(window.location.search);
  const estateId = urlParams.get('estate_id') || urlParams.get('estate') || activeEstateId || user?.estate_id;

  useEffect(() => {
    // Wait for auth to finish loading before redirecting — prevents HMR/reload from bouncing to MapView
    if (isLoadingAuth) return;

    if (!user || user.user_role !== "estate_manager") {
      navigate(createPageUrl("EstateDashboard"));
      return;
    }

    // Block access if subscription is expired (grace period is allowed to view)
    if (subscription?.isExpired) {
      navigate(createPageUrl("EstateDashboard"));
      return;
    }

    if (!estateId) {
      setError("No estate ID provided");
      setLoading(false);
      return;
    }

    generateReport();
  }, [estateId, user, subscription, isLoadingAuth]);

  useEffect(() => {
    if (!estateId || user?.user_role !== "estate_manager") return;

    captureEvent("monthly_report_opened", {
      estate_id: estateId,
    });
  }, [estateId, user?.user_role]);

  const generateReport = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        throw new Error("Not authenticated");
      }

      // Fetch contractors
      const contractorsList = await contractorsService.listByEstate(estateId);
      setContractors(contractorsList || []);

      // Fetch estate info (for logo)
      try {
        const estate = await estatesService.getById(estateId);
        setEstateInfo(estate);
      } catch {
        // Non-critical — report still works without logo
      }

      const response = await fetch(
        `${supabase.supabaseUrl}/functions/v1/generate_monthly_report`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({ estate_id: estateId }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to generate report");
      }

      const data = await response.json();
      setReportData(data);
      captureEvent("monthly_report_generated", {
        estate_id: estateId,
        total_issues: data?.summary?.total_all,
        internal_issues: data?.summary?.total_internal_issues,
        resolved_count: data?.summary?.resolved_count,
      });
    } catch (err) {
      captureEvent("monthly_report_generation_failed", {
        estate_id: estateId,
        error: err?.message,
      });
      console.error("Error generating report:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    captureEvent("monthly_report_print_clicked", {
      estate_id: estateId,
      total_issues: reportData?.summary?.total_all,
    });
    window.print();
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-blue-50 dark:from-slate-900 dark:to-slate-800">
        <Card className="p-8 text-center dark:bg-slate-800 dark:border-slate-700">
          <Loader2 className="w-12 h-12 animate-spin text-[#1883FF] mx-auto mb-4" />
          <p className="text-slate-600 dark:text-slate-300">Generating monthly report...</p>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-orange-50 to-amber-50 dark:from-slate-900 dark:to-slate-800 p-4">
        <Card className="p-8 text-center max-w-md dark:bg-slate-800 dark:border-slate-700">
          <AlertCircle className="w-12 h-12 text-red-500 dark:text-red-400 mx-auto mb-4" />
          <h2 className="text-xl font-bold mb-2 dark:text-white">Error Generating Report</h2>
          <p className="text-slate-600 dark:text-slate-400 mb-4">{error}</p>
          <Button onClick={() => navigate(createPageUrl("EstateDashboard"))} className="dark:bg-[#1883FF] dark:hover:bg-[#0066CC]">
            Back to Dashboard
          </Button>
        </Card>
      </div>
    );
  }

  if (!reportData) return null;

  const { period, summary, top_categories, internal_issues } = reportData;

  const handleUpgrade = () => {
    navigate(createPageUrl("UpgradePage"));
  };

  return (
    <div className="min-h-screen bg-white dark:bg-slate-900">
      {/* Trial Banner - Print hidden */}
      <div className="print:hidden">
        {subscription && !bannerDismissed && (
          <div className="px-4 pt-4">
            <div className="max-w-7xl mx-auto">
              <TrialBanner
                subscription={subscription}
                onUpgrade={handleUpgrade}
                onDismiss={() => setBannerDismissed(true)}
              />
            </div>
          </div>
        )}
      </div>

      {/* Print-hidden controls */}
      <div className="print:hidden sticky top-0 z-10 bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 px-4 py-3">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate(createPageUrl("EstateDashboard"))}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
          <Button 
            onClick={handlePrint} 
            disabled={!subscription?.hasWriteAccess}
            className="bg-[#1883FF] hover:bg-[#0066CC] rounded-[24px] md:rounded-[40px] disabled:opacity-50 disabled:cursor-not-allowed"
            title={!subscription?.hasWriteAccess ? "Update payment to print/save PDF" : ""}
          >
            <Printer className="w-4 h-4 mr-2" />
            Print / Save PDF
          </Button>
        </div>
      </div>

      {/* Report content */}
      <div className="max-w-7xl mx-auto p-8">
        {/* Header */}
        <div className="text-center mb-8 pb-6 border-b-2 border-slate-200 dark:border-slate-700">
          {/* Estate logo — shown on screen and in print/PDF */}
          {estateInfo?.logo_url && (
            <div className="flex justify-center mb-4">
              <img
                src={estateInfo.logo_url}
                alt={`${estateInfo.name} logo`}
                className="h-20 w-auto max-w-[160px] object-contain"
              />
            </div>
          )}
          <div className="flex items-center justify-center gap-3 mb-2">
            <h1 className="text-3xl font-bold text-slate-900 dark:text-white">Monthly Trustee Report</h1>
          </div>
          <p className="text-lg text-slate-600 dark:text-slate-400 mt-2">
            {format(new Date(period.start), "MMM d, yyyy")} - {format(new Date(period.end), "MMM d, yyyy")}
          </p>
          <p className="text-sm text-slate-500 dark:text-slate-500 mt-1">
            Generated on {format(new Date(), "PPP")}
          </p>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card className="p-4 text-center dark:bg-slate-800 dark:border-slate-700">
            <div className="text-2xl font-bold text-slate-900 dark:text-white">{summary.total_all}</div>
            <div className="text-xs text-slate-600 dark:text-slate-400 mt-1">Total Issues</div>
          </Card>
          <Card className="p-4 text-center dark:bg-slate-800 dark:border-slate-700">
            <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">{summary.total_internal_issues}</div>
            <div className="text-xs text-slate-600 dark:text-slate-400 mt-1">Internal Issues</div>
          </Card>
          <Card className="p-4 text-center dark:bg-slate-800 dark:border-slate-700">
            <div className="text-2xl font-bold text-green-600 dark:text-green-400">{summary.resolved_count}</div>
            <div className="text-xs text-slate-600 dark:text-slate-400 mt-1">Resolved</div>
          </Card>
          <Card className="p-4 text-center dark:bg-slate-800 dark:border-slate-700">
            <div className="text-2xl font-bold text-[#1883FF]">{summary.average_resolution_time}d</div>
            <div className="text-xs text-slate-600 dark:text-slate-400 mt-1">Avg. Resolution</div>
          </Card>
        </div>

        {/* Categories Breakdown */}
        <Card className="p-6 mb-8 dark:bg-slate-800 dark:border-slate-700">
          <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-4">Categories Breakdown</h2>
          <div className="space-y-3">
            {top_categories.map(({ category, count }) => {
              const percentage = (count / summary.total_all) * 100;
              return (
                <div key={category}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium text-slate-700 dark:text-slate-300 capitalize">
                      {categoryLabels[category] || category}
                    </span>
                    <span className="text-sm text-slate-600 dark:text-slate-400">{count} ({percentage.toFixed(1)}%)</span>
                  </div>
                  <div className="h-2 bg-slate-100 dark:bg-slate-700 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-[#1883FF] rounded-full transition-all"
                      style={{ width: `${percentage}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </Card>

        {/* Contractor Performance */}
        <Card className="p-6 mb-8 break-inside-avoid dark:bg-slate-800 dark:border-slate-700">
          <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-4">Contractor Performance</h2>
          {(() => {
            const contractorStats = internal_issues.reduce((acc, issue) => {
              const contractorName = issue.assigned_to || "Unassigned";
              if (!acc[contractorName]) {
                acc[contractorName] = { assigned: 0, completed: 0, totalDays: 0, resolvedCount: 0 };
              }
              acc[contractorName].assigned++;
              if (issue.status === "resolved" && issue.resolved_date) {
                acc[contractorName].completed++;
                acc[contractorName].resolvedCount++;
                const days = Math.round((new Date(issue.resolved_date) - new Date(issue.created_at)) / (1000 * 60 * 60 * 24));
                acc[contractorName].totalDays += days;
              }
              return acc;
            }, {});

            const contractorList = Object.entries(contractorStats)
              .filter(([name]) => name !== "Unassigned" && name !== "self")
              .map(([name, stats]) => {
              // Try to find contractor details by matching name
              const contractorDetails = contractors.find(c => 
                c.name.toLowerCase().trim() === name.toLowerCase().trim()
              );
              
              return {
                name,
                email: contractorDetails?.email || "—",
                phone: contractorDetails?.phone || "—",
                company: contractorDetails?.company || "—",
                specialty: contractorDetails?.specialty || "—",
                assigned: stats.assigned,
                completed: stats.completed,
                completionRate: stats.assigned > 0 ? ((stats.completed / stats.assigned) * 100).toFixed(0) : 0,
                avgDays: stats.resolvedCount > 0 ? (stats.totalDays / stats.resolvedCount).toFixed(1) : "—"
              };
            }).sort((a, b) => b.assigned - a.assigned);

            return contractorList.length === 0 ? (
              <p className="text-slate-500 dark:text-slate-400 text-center py-4">No contractor assignments this period</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200 dark:border-slate-700">
                      <th className="text-left py-2 px-2 font-semibold text-slate-700 dark:text-slate-300">Contractor</th>
                      <th className="text-left py-2 px-2 font-semibold text-slate-700 dark:text-slate-300">Company</th>
                      <th className="text-left py-2 px-2 font-semibold text-slate-700 dark:text-slate-300">Contact</th>
                      <th className="text-left py-2 px-2 font-semibold text-slate-700 dark:text-slate-300">Specialty</th>
                      <th className="text-center py-2 px-2 font-semibold text-slate-700 dark:text-slate-300">Assigned</th>
                      <th className="text-center py-2 px-2 font-semibold text-slate-700 dark:text-slate-300">Completed</th>
                      <th className="text-center py-2 px-2 font-semibold text-slate-700 dark:text-slate-300">Rate</th>
                      <th className="text-center py-2 px-2 font-semibold text-slate-700 dark:text-slate-300">Avg. Time</th>
                    </tr>
                  </thead>
                  <tbody>
                    {contractorList.map((contractor) => (
                      <tr key={contractor.name} className="border-b border-slate-100 dark:border-slate-700/50">
                        <td className="py-2 px-2 text-slate-900 dark:text-white font-medium">{contractor.name}</td>
                        <td className="py-2 px-2 text-slate-600 dark:text-slate-400 text-xs">{contractor.company}</td>
                        <td className="py-2 px-2 text-slate-600 dark:text-slate-400 text-xs">
                          {contractor.phone !== "—" && <div>{contractor.phone}</div>}
                          {contractor.email !== "—" && <div className="text-[#1883FF]">{contractor.email}</div>}
                          {contractor.phone === "—" && contractor.email === "—" && "—"}
                        </td>
                        <td className="py-2 px-2 text-slate-600 dark:text-slate-400 capitalize text-xs">{contractor.specialty}</td>
                        <td className="py-2 px-2 text-center text-slate-600 dark:text-slate-400">{contractor.assigned}</td>
                        <td className="py-2 px-2 text-center text-slate-600 dark:text-slate-400">{contractor.completed}</td>
                        <td className="py-2 px-2 text-center">
                          <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${
                            contractor.completionRate >= 80 ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' :
                            contractor.completionRate >= 50 ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400' :
                            'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                          }`}>
                            {contractor.completionRate}%
                          </span>
                        </td>
                        <td className="py-2 px-2 text-center text-slate-600 dark:text-slate-400">
                          {contractor.avgDays !== "—" ? `${contractor.avgDays}d` : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          })()}
        </Card>

        {/* Status Distribution */}
        <Card className="p-6 mb-8 break-inside-avoid dark:bg-slate-800 dark:border-slate-700">
          <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-4">Status Distribution</h2>
          {(() => {
            const statusStats = internal_issues.reduce((acc, issue) => {
              const status = issue.status || "reported";
              acc[status] = (acc[status] || 0) + 1;
              return acc;
            }, {});

            const total = internal_issues.length;
            const statusList = [
              { status: "resolved", label: "Resolved", color: "#10b981", count: statusStats.resolved || 0 },
              { status: "in_progress", label: "In Progress", color: "#3b82f6", count: statusStats.in_progress || 0 },
              { status: "dependent_on", label: "Dependant On", color: "#a855f7", count: statusStats.dependent_on || 0 },
              { status: "waiting_contractor", label: "Waiting on Contractor", color: "#f59e0b", count: statusStats.waiting_contractor || 0 },
              { status: "acknowledged", label: "Acknowledged", color: "#eab308", count: statusStats.acknowledged || 0 },
              { status: "pending_approval", label: "Pending Approval", color: "#14b8a6", count: statusStats.pending_approval || 0 },
              { status: "reported", label: "Open", color: "#ef4444", count: statusStats.reported || 0 }
            ].filter(s => s.count > 0);

            return total === 0 ? (
              <p className="text-slate-500 dark:text-slate-400 text-center py-4">No issues to display</p>
            ) : (
              <div className="flex flex-col md:flex-row items-center justify-center gap-8">
                {/* Pie Chart */}
                <div className="relative w-64 h-64">
                  <svg viewBox="0 0 200 200" className="transform -rotate-90">
                    {(() => {
                      let currentAngle = 0;
                      return statusList.map((item) => {
                        const percentage = (item.count / total) * 100;
                        const angle = (percentage / 100) * 360;
                        const x1 = 100 + 90 * Math.cos((currentAngle * Math.PI) / 180);
                        const y1 = 100 + 90 * Math.sin((currentAngle * Math.PI) / 180);
                        const x2 = 100 + 90 * Math.cos(((currentAngle + angle) * Math.PI) / 180);
                        const y2 = 100 + 90 * Math.sin(((currentAngle + angle) * Math.PI) / 180);
                        const largeArc = angle > 180 ? 1 : 0;
                        const path = `M 100 100 L ${x1} ${y1} A 90 90 0 ${largeArc} 1 ${x2} ${y2} Z`;
                        currentAngle += angle;
                        return (
                          <path
                            key={item.status}
                            d={path}
                            fill={item.color}
                            stroke="white"
                            strokeWidth="2"
                          />
                        );
                      });
                    })()}
                  </svg>
                </div>
                {/* Legend */}
                <div className="space-y-3">
                  {statusList.map((item) => {
                    const percentage = ((item.count / total) * 100).toFixed(1);
                    return (
                      <div key={item.status} className="flex items-center gap-3">
                        <div className="w-4 h-4 rounded" style={{ backgroundColor: item.color }}></div>
                        <div className="flex-1">
                          <div className="text-sm font-medium text-slate-900 dark:text-white">{item.label}</div>
                          <div className="text-xs text-slate-600 dark:text-slate-400">{item.count} issues ({percentage}%)</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })()}
        </Card>

        {/* Internal Issues */}
        <Card className="p-6 break-inside-avoid dark:bg-slate-800 dark:border-slate-700">
          <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-4">
            Internal Issues ({internal_issues.length})
          </h2>
          {internal_issues.length === 0 ? (
            <p className="text-slate-500 dark:text-slate-400 text-center py-4">No internal issues this period</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200 dark:border-slate-700">
                    <th className="text-left py-2 px-2 font-semibold text-slate-700 dark:text-slate-300">Title</th>
                    <th className="text-left py-2 px-2 font-semibold text-slate-700 dark:text-slate-300">Category</th>
                    <th className="text-left py-2 px-2 font-semibold text-slate-700 dark:text-slate-300">Dependency</th>
                    <th className="text-left py-2 px-2 font-semibold text-slate-700 dark:text-slate-300">Assigned To</th>
                    <th className="text-left py-2 px-2 font-semibold text-slate-700 dark:text-slate-300">Status</th>
                    <th className="text-left py-2 px-2 font-semibold text-slate-700 dark:text-slate-300">Created</th>
                    <th className="text-left py-2 px-2 font-semibold text-slate-700 dark:text-slate-300">Resolved</th>
                  </tr>
                </thead>
                <tbody>
                  {internal_issues.map((issue) => {
                    const dependencyIssue = internal_issues.find(
                      (candidate) => candidate.id === issue.depends_on_issue_id
                    );
                    const dependencyTitle = issue.depends_on_issue_title || dependencyIssue?.title;
                    const statusLabel = issue.status === 'dependent_on'
                      ? (dependencyTitle ? `Dependant On: ${dependencyTitle}` : 'Dependant On')
                      : issue.status?.replace(/_/g, ' ');

                    return (
                    <tr key={issue.id} className="border-b border-slate-100 dark:border-slate-700/50">
                      <td className="py-2 px-2 text-slate-900 dark:text-white">{issue.title}</td>
                      <td className="py-2 px-2 text-slate-600 dark:text-slate-400 capitalize">
                        {categoryLabels[issue.category] || issue.category}
                      </td>
                      <td className="py-2 px-2 text-slate-600 dark:text-slate-400 text-xs">
                        {dependencyTitle || "—"}
                      </td>
                      <td className="py-2 px-2 text-slate-600 dark:text-slate-400 capitalize">
                        {issue.assigned_to || "Unassigned"}
                      </td>
                      <td className="py-2 px-2">
                        <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${statusColors[issue.status]?.bg || 'bg-gray-100'} ${statusColors[issue.status]?.text || 'text-gray-700'}`}>
                          {statusLabel}
                        </span>
                      </td>
                      <td className="py-2 px-2 text-slate-600 dark:text-slate-400">
                        {format(new Date(issue.created_at), "MMM d, yyyy")}
                      </td>
                      <td className="py-2 px-2 text-slate-600">
                        {issue.resolved_date ? format(new Date(issue.resolved_date), "MMM d, yyyy") : "—"}
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        {/* Footer */}
        <div className="mt-8 pt-6 border-t border-slate-200 dark:border-slate-700 text-center text-xs text-slate-500 dark:text-slate-500">
          <p>This report was automatically generated by FixMyHood Estate Management System</p>
        </div>
      </div>

      {/* Print styles */}
      <style>{`
        @media print {
          body {
            print-color-adjust: exact;
            -webkit-print-color-adjust: exact;
          }
          .print\\:hidden {
            display: none !important;
          }
          @page {
            margin: 1.5cm;
            size: A4;
          }
          .break-inside-avoid {
            break-inside: avoid;
            page-break-inside: avoid;
          }
          /* Prevent table content from being clipped */
          .overflow-x-auto {
            overflow: visible !important;
          }
          /* Use full page width */
          .max-w-7xl {
            max-width: 100% !important;
          }
          /* Compact card spacing for print */
          .p-8 { padding: 0.75rem !important; }
          .p-6 { padding: 0.6rem !important; }
          .mb-8 { margin-bottom: 0.6rem !important; }
          .gap-4 { gap: 0.4rem !important; }
          /* Smaller table text to fit columns */
          table { font-size: 10px !important; width: 100% !important; }
          th, td { padding: 3px 5px !important; }
          /* Reduce chart size on print */
          .w-64 { width: 10rem !important; }
          .h-64 { height: 10rem !important; }
        }
      `}</style>
    </div>
  );
}
